"use client";

import React, { FC } from "react";
import { AppstoreOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Space, Typography } from "antd";
import {
  ComponentFactoryArguments,
  IToolboxComponent,
  ShaIcon,
  useFormExpression,
  validateConfigurableComponentSettings,
} from "@shesha-io/reactjs";
import type { IButtonItem } from "@shesha-io/reactjs";
import { IButtonDropdownProps } from "./model";
import { getSettings } from "./settings";

const renderIcon = (icon?: string | React.ReactNode) => {
  if (!icon) return undefined;
  if (typeof icon === "string") return <ShaIcon iconName={icon as any} />;
  return icon;
};

/** antd 5 dropped "ghost" as a button type — translate it to default + ghost prop */
const resolveButtonType = (type?: string) =>
  (type === "ghost" ? "default" : type ?? "default") as any;

const ButtonDropdownControl: FC<IButtonDropdownProps> = (model) => {
  const { executeActionViaConfiguration } = useFormExpression();

  const items = (model.items ?? []).filter((item) => !item.hidden) as IButtonItem[];

  const overlay = (
    <div
      style={{
        background: "var(--ant-color-bg-elevated, #fff)",
        padding: 8,
        borderRadius: 8,
        boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
        minWidth: 160,
      }}
    >
      <Space direction="vertical" style={{ display: "flex" }} size={4}>
        {items.length === 0 ? (
          <Typography.Text type="secondary">No buttons configured</Typography.Text>
        ) : (
          items.map((item) => (
            <Button
              key={item.id}
              block
              type={resolveButtonType(item.buttonType)}
              ghost={item.buttonType === "ghost" || item.ghost}
              danger={item.danger}
              size={item.size}
              icon={renderIcon(item.icon)}
              title={item.tooltip}
              onClick={() => {
                if (item.actionConfiguration) {
                  executeActionViaConfiguration(item.actionConfiguration);
                }
              }}
            >
              {item.label as React.ReactNode}
            </Button>
          ))
        )}
      </Space>
    </div>
  );

  return (
    <Dropdown
      trigger={[model.trigger ?? "click"]}
      placement={model.placement ?? "bottomLeft"}
      dropdownRender={() => overlay}
    >
      <Button
        type={resolveButtonType(model.buttonType)}
        size={model.size}
        danger={model.danger}
        block={model.block}
        icon={renderIcon(model.icon)}
      >
        <Space size={6}>
          {model.caption ?? "Actions"}
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );
};

const ButtonDropdownComponent: IToolboxComponent<IButtonDropdownProps> = {
  type: "buttonDropdown",
  name: "Button Dropdown",
  icon: <AppstoreOutlined />,
  isInput: false,
  isOutput: false,
  Factory: ({ model }: ComponentFactoryArguments<IButtonDropdownProps>) => {
    if (model.hidden) return null;
    return <ButtonDropdownControl {...model} />;
  },
  settingsFormMarkup: getSettings(),
  validateSettings: (model) => validateConfigurableComponentSettings(getSettings(), model),
  migrator: (m) =>
    m.add<IButtonDropdownProps>(0, (prev) => {
      const p = prev as IButtonDropdownProps;
      return {
        ...p,
        caption: p.caption ?? "Actions",
        buttonType: p.buttonType ?? "default",
        trigger: p.trigger ?? "click",
        placement: p.placement ?? "bottomLeft",
        items: p.items ?? [],
      };
    }),
};

export default ButtonDropdownComponent;
